import { getInputProps } from "@/utils/helpers/columnsHelper";
import { Box, Stack, FormControlLabel, Switch, Typography, Alert, Skeleton } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import {
  useGetOne,
  useGetList,
  ReferenceInput,
  AutocompleteInput,
  TextInput,
  DateInput,
  BooleanInput,
  SelectInput,
  useGetIdentity,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { buildTemplateFilter, addDaysUTC } from "../utils/taskUtils";
import {
  AlertType,
  ObjectType,
  ResourceName,
  TaskStatus,
  TASK_STATUS_CHOICES,
  UserStatus,
  type TaskTemplate,
} from "@/types";

const getTaskInputProps = (key: string, textReplace = {}) => getInputProps(key, textReplace, ResourceName.TASKS);

export const TaskCreateInputs = () => {
  const { setValue } = useFormContext();
  const { data: currentUser } = useGetIdentity();
  const [useTemplate, setUseTemplate] = useState(false);

  // Watch values for conditional logic
  const assignedUserId = useWatch({ name: "assigned_user_id_ref" });
  const recordObjectType = useWatch({ name: "record.object_type" });
  const recordObjectId = useWatch({ name: "record.object_id" });
  const templateIdRef = useWatch({ name: "template_id_ref" });

  // Fetch all active templates to determine which record types have templates
  const { data: allTemplates = [] } = useGetList<TaskTemplate>(
    "task-templates",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "title", order: "ASC" },
      filter: { is_active: true },
    },
    { enabled: useTemplate }
  );

  // Extract unique object types that have templates
  const availableObjectTypes = useMemo(() => {
    const objectTypesSet = new Set<ObjectType>();
    allTemplates.forEach((template: TaskTemplate) => {
      if (template.applicable_object_types) {
        if (Array.isArray(template.applicable_object_types)) {
          template.applicable_object_types.forEach((type: ObjectType) => objectTypesSet.add(type));
        } else if (typeof template.applicable_object_types === "string") {
          template.applicable_object_types
            .split(",")
            .forEach((type: string) => objectTypesSet.add(type.trim() as ObjectType));
        }
      }
    });
    return objectTypesSet;
  }, [allTemplates]);

  // Auto-fill record label when object is selected
  const { data: recordObject } = useGetOne(
    recordObjectType,
    { id: recordObjectId },
    { enabled: !!recordObjectType && !!recordObjectId }
  );

  // Fetch selected template details
  const { data: selectedTemplate, isLoading: isLoadingTemplate } = useGetOne(
    "task-templates",
    { id: templateIdRef },
    { enabled: !!templateIdRef }
  );

  // Auto-fill form when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      // Validate that template matches current context
      let applicableTypes: string[] = [];

      if (Array.isArray(selectedTemplate.applicable_object_types)) {
        applicableTypes = selectedTemplate.applicable_object_types;
      } else if (typeof selectedTemplate.applicable_object_types === "string") {
        applicableTypes = selectedTemplate.applicable_object_types.split(",").map((t: string) => t.trim());
      }

      if (recordObjectType && applicableTypes.length > 0 && !applicableTypes.includes(recordObjectType)) {
        // Template doesn't match current context - clear it
        setValue("template_id_ref", null, {
          shouldDirty: false,
          shouldValidate: false,
        });
        return;
      }

      // Set title and description
      setValue("title", selectedTemplate.title, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue("description", selectedTemplate.description || "", {
        shouldDirty: true,
        shouldValidate: false,
      });

      // Set status and completed flag
      setValue("status", TaskStatus.OPEN, {
        shouldDirty: true,
        shouldValidate: false,
      });
      setValue("completed", false, {
        shouldDirty: true,
        shouldValidate: false,
      });

      // Set due date if default_due_days is present
      if (selectedTemplate.default_due_days && typeof selectedTemplate.default_due_days === "number") {
        const dueDate = addDaysUTC(selectedTemplate.default_due_days);
        setValue("due_date", dueDate, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }

      // Set display_claim_exhibit
      setValue("display_claim_exhibit", selectedTemplate.default_show_on_claim_exhibit ?? true, {
        shouldDirty: true,
        shouldValidate: false,
      });

      // Set assigned_role_id_ref if present and valid
      if (
        selectedTemplate.auto_assign_role_id_ref &&
        typeof selectedTemplate.auto_assign_role_id_ref === "string" &&
        selectedTemplate.auto_assign_role_id_ref.trim() !== ""
      ) {
        setValue("assigned_role_id_ref", selectedTemplate.auto_assign_role_id_ref, {
          shouldDirty: true,
          shouldValidate: false,
        });
      } else {
        // Clear it if not present or invalid
        setValue("assigned_role_id_ref", null, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }

      // Clear assigned_user_id_ref to allow role assignment
      setValue("assigned_user_id_ref", null, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [selectedTemplate, setValue, recordObjectType]);

  // Clear template when context changes
  useEffect(() => {
    if (templateIdRef) {
      // Clear template_id_ref and reset fields when object type changes
      setValue("template_id_ref", null, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [recordObjectType, recordObjectId, setValue]);

  useEffect(() => {
    if (recordObject && recordObjectType) {
      let label = "";
      // Try to create a meaningful label based on object type
      switch (recordObjectType) {
        case ObjectType.STOPLOSS_CLAIMS:
          label = `${recordObject.aegle_id || recordObject.id} - ${recordObject.stoploss_claim_type || "Unknown"} Claim`;
          break;
        case ObjectType.CLAIMANTS:
          label = `${recordObject.claimant_name || recordObject.aegle_id || recordObject.id}`;
          break;
        case ObjectType.POLICIES:
          label = `${recordObject.aegle_id || recordObject.id} - Policy`;
          break;
        case ObjectType.CLIENTS:
          label = `${recordObject.client_legal_name || recordObject.aegle_id || recordObject.id}`;
          break;
        case ObjectType.REIMBURSEMENTS:
          label = `${recordObject.aegle_id || recordObject.id} - Reimbursement`;
          break;
        case ObjectType.ENDORSEMENTS:
          label = `${recordObject.aegle_id || recordObject.id} - Endorsement`;
          break;
        case ObjectType.DOCUMENTS:
          label = `${recordObject.title || recordObject.aegle_id || recordObject.id}`;
          break;
        default:
          label = `${recordObject.aegle_id || recordObject.name || recordObject.title || recordObject.id}`;
      }

      setValue("record.label", label, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [recordObject, recordObjectType, setValue]);

  // All possible record type choices
  const allRecordTypeChoices: Array<{ id: ObjectType; name: string }> = [
    { id: ObjectType.STOPLOSS_CLAIMS, name: "Stop-Loss Claim" },
    { id: ObjectType.REIMBURSEMENTS, name: "Reimbursement" },
    { id: ObjectType.CLAIMANTS, name: "Claimant" },
    { id: ObjectType.CLIENTS, name: "Client" },
    { id: ObjectType.POLICIES, name: "Policy" },
    { id: ObjectType.ENDORSEMENTS, name: "Endorsement" },
    { id: ObjectType.DOCUMENTS, name: "Document" },
  ];

  // Filter record types when using template mode
  const recordTypeChoices = useTemplate
    ? allRecordTypeChoices.filter((choice) => availableObjectTypes.has(choice.id))
    : allRecordTypeChoices;

  // Show message if no templates available
  const noTemplatesAvailable = useTemplate && recordTypeChoices.length === 0;

  // Render Related Record Section
  const renderRelatedRecordSection = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Related Record
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Link this task to a specific record (claim, claimant, policy, etc.)
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <SelectInput
            {...getTaskInputProps("record.object_type")}
            label="Record Type"
            choices={recordTypeChoices}
            size="small"
            sx={{ m: 0 }}
          />
        </Box>

        {recordObjectType && (
          <Box sx={{ flex: 1 }}>
            <ReferenceInput source="record.object_id" reference={recordObjectType.replace(/_/g, "-")}>
              <AutocompleteInput
                {...getTaskInputProps("record.object_id")}
                label="Select Record"
                size="small"
                optionText={(choice) => {
                  switch (recordObjectType) {
                    case ObjectType.STOPLOSS_CLAIMS:
                      return `${choice.aegle_id || choice.id} - ${choice.stoploss_claim_type || "Unknown"} (${choice.claim_status || "Unknown"})`;
                    case ObjectType.CLAIMANTS:
                      return `${choice.claimant_name || choice.aegle_id || choice.id}`;
                    case ObjectType.POLICIES:
                      return `${choice.aegle_id || choice.id} - Policy`;
                    case ObjectType.CLIENTS:
                      return `${choice.client_legal_name || choice.aegle_id || choice.id}`;
                    case ObjectType.DOCUMENTS:
                      return `${choice.title || choice.aegle_id || choice.id}`;
                    default:
                      return choice.name || choice.title || choice.aegle_id || choice.id;
                  }
                }}
              />
            </ReferenceInput>
          </Box>
        )}
      </Stack>

      {recordObjectType && <BooleanInput {...getTaskInputProps("display_claim_exhibit")} defaultValue={true} />}
    </Box>
  );

  // Render Task Details Section
  const renderTaskDetailsSection = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Task Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Provide the title, description, and timeline for this task
      </Typography>

      {isLoadingTemplate ? (
        <>
          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1, mt: 2 }} />
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, flex: 1 }} />
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, flex: 1 }} />
          </Stack>
        </>
      ) : (
        <>
          <TextInput {...getTaskInputProps("title")} required fullWidth size="small" />

          <TextInput {...getTaskInputProps("description")} multiline rows={3} fullWidth size="small" sx={{ mt: 2 }} />

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <SelectInput
              {...getTaskInputProps("status")}
              choices={TASK_STATUS_CHOICES}
              defaultValue={TaskStatus.OPEN}
              size="small"
              sx={{ flex: 1 }}
            />

            <DateInput {...getTaskInputProps("due_date")} size="small" sx={{ flex: 1 }} />
          </Stack>
        </>
      )}
    </Box>
  );

  // Render Assignment Section
  const renderAssignmentSection = () => (
    <Box>
      <Typography variant="h5" gutterBottom>
        Assignment
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Assign this task to either a specific user OR a role (not both). User assignment takes precedence.
      </Typography>

      {isLoadingTemplate ? (
        <Stack direction="row" spacing={2}>
          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, flex: 1 }} />
          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, flex: 1 }} />
        </Stack>
      ) : (
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <ReferenceInput
              source="assigned_user_id_ref"
              reference={ResourceName.USERS}
              filter={{ status: UserStatus.ACTIVE }}
            >
              <AutocompleteInput
                {...getTaskInputProps("assigned_user_id_ref")}
                label="Assign to User"
                size="small"
                optionText={(choice) => {
                  const userName = choice.full_name || choice.email || choice.id;
                  const isCurrentUser = currentUser && choice.id === currentUser.id;
                  return isCurrentUser ? `${userName} (me)` : userName;
                }}
              />
            </ReferenceInput>
          </Box>

          {!assignedUserId && (
            <Box sx={{ flex: 1 }}>
              <ReferenceInput source="assigned_role_id_ref" reference={ResourceName.ROLES}>
                <AutocompleteInput
                  {...getTaskInputProps("assigned_role_id_ref")}
                  label="Or Assign to Role"
                  size="small"
                  optionText="name"
                />
              </ReferenceInput>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );

  return (
    <>
      {/* Sticky template toggle */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "background.paper",
          zIndex: 1,
          borderBottom: 1,
          borderColor: "divider",
          pb: 1.5,
          mb: 2,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={useTemplate}
              onChange={(e) => {
                setUseTemplate(e.target.checked);
                if (!e.target.checked) {
                  setValue("template_id_ref", null, {
                    shouldDirty: false,
                    shouldValidate: false,
                  });
                }
              }}
              color="primary"
            />
          }
          label="Create from Template"
        />
      </Box>

      {noTemplatesAvailable ? (
        <Alert severity={AlertType.INFO} sx={{ mb: 2 }}>
          No task templates are currently available. Please create templates first or toggle off "Create from Template".
        </Alert>
      ) : (
        <>
          {/* Related Record Section - Common to both flows */}
          {renderRelatedRecordSection()}

          {/* Template Selector - Only in template mode */}
          {useTemplate && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Template
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                Select a template to auto-fill task details
              </Typography>

              <ReferenceInput
                label="Template"
                reference="task-templates"
                source="template_id_ref"
                filter={buildTemplateFilter(recordObjectType)}
                perPage={50}
              >
                <AutocompleteInput
                  label="Template (optional)"
                  size="small"
                  fullWidth
                  disabled={!recordObjectType}
                  optionText="title"
                  helperText={
                    !recordObjectType
                      ? "Select a record type above to load templates"
                      : "Select a template to auto-fill task details"
                  }
                />
              </ReferenceInput>
            </Box>
          )}

          {/* Task Details Section - Common to both flows */}
          {renderTaskDetailsSection()}

          {/* Assignment Section - Common to both flows */}
          {renderAssignmentSection()}
        </>
      )}
    </>
  );
};
